import React from 'react';
import {
  FiEdit2 as Edit,
  FiTrash2 as Trash2,
  FiFileText as FileText,
  FiMessageCircle as MessageCircle,
  FiCode as Code,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

/**
 * Generic table for displaying dataset items of any type
 * (documents, qa_pairs, structured, or mixed)
 */

interface DatasetItemTypeSpecific {
  title?: string;
  question?: string;
  answer?: string;
}

interface DatasetItemMetadata {
  tags?: string[];
}

interface DatasetItem {
  id: string;
  name?: string;
  content?: string;
  item_type?: string;
  type_specific_data?: DatasetItemTypeSpecific;
  metadata?: DatasetItemMetadata;
}

interface EmptyMessageConfig {
  icon: IconType;
  title: string;
  subtitle: string;
}

interface ItemPreview {
  primary: string;
  secondary: string;
}

interface DatasetItemsTableProps {
  items: DatasetItem[];
  loading: boolean;
  contentType: string;
  onEdit: (item: DatasetItem) => void;
  onDelete: (item: DatasetItem) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
}

const DatasetItemsTable: React.FC<DatasetItemsTableProps> = ({
  items,
  loading,
  contentType,
  onEdit,
  onDelete,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
}) => {
  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const someSelected = items.some((item) => selectedIds.has(item.id));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange?.(new Set());
    } else {
      onSelectionChange?.(new Set(items.map((item) => item.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.(next);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (items.length === 0) {
    const emptyMessages: Record<string, EmptyMessageConfig> = {
      documents: {
        icon: FileText,
        title: 'No documents yet',
        subtitle: 'Add your first document for RAG',
      },
      qa_pairs: {
        icon: MessageCircle,
        title: 'No Q&A pairs yet',
        subtitle: 'Add question-answer pairs for training',
      },
      mixed: {
        icon: Code,
        title: 'No items yet',
        subtitle: 'Add items of any type to this dataset',
      },
    };
    const config = emptyMessages[contentType] ?? emptyMessages.mixed;
    const Icon = config.icon;

    return (
      <div className="text-center py-12">
        <Icon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{config.title}</h3>
        <p className="text-gray-600 dark:text-gray-400">{config.subtitle}</p>
      </div>
    );
  }

  const getItemTypeIcon = (itemType: string | undefined): React.ReactElement => {
    switch (itemType) {
      case 'document':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'qa_pair':
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      case 'structured':
        return <Code className="w-4 h-4 text-purple-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getItemTypeBadgeClass = (itemType: string | undefined): string => {
    const colors: Record<string, string> = {
      document: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      qa_pair: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      structured: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
    };
    return colors[itemType ?? ''] ?? 'bg-gray-100 text-gray-800';
  };

  const getItemTypeLabel = (itemType: string | undefined): string => {
    const labels: Record<string, string> = {
      document: 'Document',
      qa_pair: 'Q&A',
      structured: 'Structured',
    };
    return labels[itemType ?? ''] ?? (itemType ?? '');
  };

  const getItemTypeBadge = (item: DatasetItem): React.ReactElement => (
    <span
      className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getItemTypeBadgeClass(item.item_type)}`}
    >
      {getItemTypeIcon(item.item_type)}
      {getItemTypeLabel(item.item_type)}
    </span>
  );

  const getTags = (item: DatasetItem): string[] => item.metadata?.tags ?? [];

  const getPreview = (item: DatasetItem): ItemPreview => {
    switch (item.item_type) {
      case 'document': {
        const docTitle = item.type_specific_data?.title || item.name || 'Untitled';
        const docContent = item.content ?? '';
        return {
          primary: docTitle,
          secondary: docContent.substring(0, 100) + (docContent.length > 100 ? '...' : ''),
        };
      }
      case 'qa_pair': {
        const question = item.type_specific_data?.question ?? '';
        const answer = item.type_specific_data?.answer ?? '';
        return {
          primary:
            question.substring(0, 60) + (question.length > 60 ? '...' : '') || item.name || '',
          secondary: `A: ${answer.substring(0, 80)}${answer.length > 80 ? '...' : ''}`,
        };
      }
      case 'structured': {
        const structContent = item.content ?? '';
        return {
          primary: item.name || 'Structured Data',
          secondary: structContent.substring(0, 100) + (structContent.length > 100 ? '...' : ''),
        };
      }
      default: {
        const defaultContent = item.content ?? '';
        return {
          primary: item.name || 'Untitled',
          secondary:
            defaultContent.substring(0, 100) + (defaultContent.length > 100 ? '...' : ''),
        };
      }
    }
  };

  // For documents/qa_pairs, use a card-like layout
  if (['documents', 'qa_pairs'].includes(contentType)) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {selectable && items.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el: HTMLInputElement | null) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={toggleAll}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />{' '}
              Select all
            </label>
          </div>
        )}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => {
            const preview = getPreview(item);
            const tags = getTags(item);

            return (
              <div
                key={item.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  {selectable && (
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleOne(item.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getItemTypeIcon(item.item_type)}
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {preview.primary}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {preview.secondary}
                    </p>
                    {tags.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          >
                            {tag}
                          </span>
                        ))}
                        {tags.length > 3 && (
                          <span className="text-xs text-gray-400">+{tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => onEdit(item)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(item)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // For mixed/structured, use a table layout
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el: HTMLInputElement | null) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAll}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Name / Content
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => {
            const preview = getPreview(item);
            const tags = getTags(item);

            return (
              <tr
                key={item.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {selectable && (
                  <td className="px-4 py-4 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">{getItemTypeBadge(item)}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {preview.primary}
                  </div>
                  {preview.secondary && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                      {preview.secondary}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-1 flex-wrap">
                    {tags.length > 0 ? (
                      tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                    {tags.length > 2 && (
                      <span className="text-xs text-gray-400">+{tags.length - 2}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(item)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DatasetItemsTable;
